'use client';

import { usePersistentVoiceCallOptional } from '@/contexts/persistent-voice-call-context';
import { usePathname } from 'next/navigation';
import { getCanonicalThreadPath } from '@/lib/thread-routes';
import { Skeleton } from '@/components/ui/skeleton';
import {
  THREAD_SKELETON_PLACEMENT_CLASS,
  ThreadTranscriptSkeleton,
} from '@/components/org-chat/thread-transcript-skeleton';
import { type ReactElement, type ReactNode } from 'react';

function ChatTopFade(): ReactElement {
  return (
    <div className="workspace-canvas-fade-top pointer-events-none absolute inset-x-0 top-0 h-12" />
  );
}

function ChatBottomFade(): ReactElement {
  return (
    <div className="workspace-canvas-fade-bottom pointer-events-none absolute inset-x-0 bottom-0 h-16" />
  );
}

/**
 * Pure thread loading column (no hints, providers or queries), shared by the
 * route loading layout and the workspace admission shell.
 */
export function ThreadLoadingColumn({
  startupPending = false,
  headerStart = null,
}: {
  /**
   * A just-sent thread is arriving with its startup handoff: leave the
   * transcript empty instead of flashing placeholder rows for a few frames
   * before the prompt renders.
   */
  startupPending?: boolean;
  /** The mobile sidebar toggle, where the loaded header has it. */
  headerStart?: ReactNode;
}) {
  const call = usePersistentVoiceCallOptional()?.call;
  const pathname = usePathname();
  const voiceStartup = Boolean(
    call && pathname === getCanonicalThreadPath(call.orgSlug, call.routeRef)
  );
  return (
    <div
      data-thread-loading-shell="messages"
      className="workspace-canvas-surface relative flex h-full min-h-0 overflow-hidden"
    >
      <div className="absolute top-0 right-0 left-0 z-50 px-2 py-1">
        <ChatTopFade />
        <div className="relative z-[100] flex items-center justify-between gap-1">
          <div className="flex items-center">
            {headerStart ?? <div className="h-8 w-8 lg:hidden" />}
          </div>
          <div className="min-w-0 flex-1" />
          <div className="flex items-center gap-1">
            <div className="h-8 w-8" />
          </div>
        </div>
      </div>
      <div className="min-h-0 w-full flex-1 overflow-y-auto px-4 pt-16 pb-40">
        {/* Same geometry and centring as the transcript's loading skeleton
            (org-message-list-content), so the hand-off between the two does
            not move and the morph lands on the real turn. */}
        <div className="flex min-h-[max(0px,calc(88dvh-18rem))] flex-col">
          <div className={THREAD_SKELETON_PLACEMENT_CLASS}>
            {startupPending || voiceStartup ? null : (
              <ThreadTranscriptSkeleton shapeAware />
            )}
          </div>
        </div>
      </div>

      {!voiceStartup && (
        <div className="absolute right-0 bottom-0 left-0 z-10 px-2 pt-8 pb-2 sm:px-4">
          <ChatBottomFade />
          <div className="relative mx-auto max-w-xl md:py-4">
            <Skeleton className="h-12 w-full rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
}
